import { Injectable, inject, signal } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
  Router,
} from '@angular/router';
import { LoaderService } from './loader.service';

export const ROUTE_LOADER_DELAY_MS = 150;

@Injectable({ providedIn: 'root' })
export class PageNavigationService {
  private readonly router = inject(Router);
  private readonly loader = inject(LoaderService);

  private readonly _changing = signal(false);

  public readonly changing = this._changing.asReadonly();

  private routeLoaderTimer: ReturnType<typeof setTimeout> | null = null;
  private routeLoaderShown = false;

  constructor() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.onStart(event.url);
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError ||
        event instanceof NavigationSkipped
      ) {
        this.onFinish();
      }
    });
  }

  private onStart(targetUrl: string): void {
    const isSameRoute = this.isSameRoute(targetUrl);
    this._changing.set(isSameRoute);

    if (!isSameRoute) {
      this.scheduleRouteLoader();
    }
  }

  private onFinish(): void {
    this._changing.set(false);
    this.clearRouteLoader();
  }

  private scheduleRouteLoader(): void {
    if (this.routeLoaderTimer !== null || this.routeLoaderShown) {
      return;
    }

    this.routeLoaderTimer = setTimeout(() => {
      this.routeLoaderTimer = null;
      this.routeLoaderShown = true;
      this.loader.begin();
    }, ROUTE_LOADER_DELAY_MS);
  }

  private clearRouteLoader(): void {
    if (this.routeLoaderTimer !== null) {
      clearTimeout(this.routeLoaderTimer);
      this.routeLoaderTimer = null;
    }

    if (this.routeLoaderShown) {
      this.routeLoaderShown = false;
      this.loader.end();
    }
  }

  private isSameRoute(targetUrl: string): boolean {
    return this.pathOf(targetUrl) === this.pathOf(this.router.url);
  }

  private pathOf(url: string): string {
    return url.split(/[?#]/)[0];
  }
}
